'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { authBunja } from '@flipster/features/src/shared/auth/state';
import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { useDepositModal } from '@flipster/features/src/shared/wallet-modal/deposit';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system/button';
import { hstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { EarnEvent } from '@flipster/utils/segment/events/earn';

import { mintBunja } from '@web/app/[locale]/(web)/_modal/MintFlowModal/state';

import { EarnActionModal } from '../../earn-action-modal';

export const ActionButtons = ({ currency }: { currency: string }) => {
  const t = useTranslations();
  const overlay = useOverlay();
  const router = useRouter();
  const openDepositModal = useDepositModal();
  const { isLoggedInAtom } = useBunja(authBunja);
  const { openMintModal } = useBunja(mintBunja);
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);
  const { isMintAvailable, isTradeAvailable } = getEarnSummaryOfCurrency(currency, earnSummary);

  const needToOpenActions = isMintAvailable || isTradeAvailable;
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  const onClickSubscribe = () => {
    segment.track(EarnEvent.EarnManageClick, { button_text: 'Subscribe', currency });
    overlay.open(({ isOpen, onClose, unmount }) => (
      <EarnActionModal isOpen={isOpen} onClose={onClose} currency={currency} onExit={unmount} />
    ));
  };

  const onClickRedeem = () => {
    segment.track(EarnEvent.EarnManageClick, { button_text: 'Redeem', currency });
    openMintModal({ providerCurrency: currency, flowType: 'REDEEM' });
  };

  if (needToOpenActions) {
    return (
      <div className={hstack({ justify: 'center', gap: 8 })}>
        {isMintAvailable && (
          <Button
            label={t('Common.r251008.Redeem')}
            hierarchy='secondary'
            color='neutral'
            onClick={onClickRedeem}
            css={{ w: { base: '50%', tablet: 160 } }}
          />
        )}
        <Button
          label={t('Autogen_common.r251126.s_Add')}
          onClick={onClickSubscribe}
          css={{ w: isMintAvailable ? { base: '50%', tablet: 160 } : { base: '100%', tablet: 296 } }}
        />
      </div>
    );
  }

  return (
    <div className={hstack({ justify: 'center' })}>
      <Button
        hierarchy='primary'
        color='accent'
        size='medium'
        label={<FormattedMessage id='Common.r251107.Deposit' />}
        css={{ w: { base: '100%', tablet: 296 } }}
        onClick={() => {
          if (isLoggedIn) {
            segment.track(EarnEvent.EarnManageClick, { button_text: 'Deposit', currency });
            openDepositModal({ defaultValues: { currency } });
          } else {
            router.push('/login');
          }
        }}
      />
    </div>
  );
};
